import React, { useCallback, useMemo, useState } from 'react'
import {
  Dropdown as DropdownBase,
  DropdownToggle as DropdownToggleBase,
  DropdownItem as DropdownItemBase,
  DropdownProps,
  UncontrolledAlert,
} from 'reactstrap'
import { useRecoilState } from 'recoil'
import { SearchBox } from 'src/components/Common/SearchBox'
import { LinkExternal } from 'src/components/Link/LinkExternal'
import { search } from 'src/helpers/search'
import { useTranslationSafe } from 'src/helpers/useTranslationSafe'
import styled from 'styled-components'
import { DropdownMenuInViewport } from 'src/components/Common/DropdownMenuInViewport'
import { localeAtom } from 'src/state/locale.state'
import { getLocaleWithKey, Locale, localesArray } from 'src/i18n/i18n'

export type LanguageSwitcherProps = DropdownProps

export function LanguageSwitcher({ ...restProps }: LanguageSwitcherProps) {
  const { t } = useTranslationSafe()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentLocale, setCurrentLocale] = useRecoilState(localeAtom)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const toggle = useCallback(() => setDropdownOpen((prevState) => !prevState), [])
  const setLocaleLocal = useCallback((locale: Locale) => () => setCurrentLocale(locale.key), [setCurrentLocale])

  const localesFiltered = useMemo(() => {
    if (searchTerm.trim().length === 0) {
      return localesArray
    }
    const { itemsStartWith, itemsInclude } = search(localesArray, searchTerm, (locale) => [
      locale.name,
      locale.native,
      locale.key,
    ])
    return [...itemsStartWith, ...itemsInclude]
  }, [searchTerm])

  return (
    <Dropdown nav isOpen={dropdownOpen} toggle={toggle} {...restProps}>
      <DropdownToggle nav>
        <LabelShort locale={currentLocale} />
      </DropdownToggle>
      <DropdownMenu right>
        <SearchBoxWrapper>
          <SearchBox searchTitle={t('Search languages')} searchTerm={searchTerm} onSearchTermChange={setSearchTerm} />
        </SearchBoxWrapper>
        <UncontrolledAlert
          closeClassName="d-none"
          fade={false}
          color="warning"
          className="m-0 mb-2 mr-3 py-1 px-2 small"
        >
          {t(
            'All text except English is generated using machine translation. Accuracy is not guaranteed. Please submit your fixes {{ here }}',
          )}
          <LinkExternal href="https://github.com/nextstrain/nextclade/blob/master/docs/dev/developer-guide.md#internationalization-translation">
            {t('here')}
          </LinkExternal>
        </UncontrolledAlert>
        <DropdownMenuListWrapper>
          {localesFiltered.map((locale) => {
            const isCurrent = locale.key === currentLocale
            return (
              <DropdownItem active={isCurrent} key={locale.key} onClick={setLocaleLocal(locale)}>
                <LanguageSwitcherItem locale={locale.key} />
              </DropdownItem>
            )
          })}
        </DropdownMenuListWrapper>
      </DropdownMenu>
    </Dropdown>
  )
}

export function LanguageSwitcherItem({ locale }: { locale: string }) {
  const { name, native } = getLocaleWithKey(locale)
  const { label, tooltip } = useMemo(() => {
    return { label: `(${native})`, tooltip: `${name} (${native})` }
  }, [name, native])
  return (
    <LanguageSwitcherItemWrapper title={tooltip}>
      <LabelShort locale={locale} />
      <span className="mx-2">{label}</span>
    </LanguageSwitcherItemWrapper>
  )
}

export function LabelShort({ locale, ...restProps }: { locale: string; className?: string }) {
  const { key } = getLocaleWithKey(locale)
  const { t } = useTranslationSafe()
  return (
    <LabelShortText title={t('Change language')} {...restProps}>
      {key}
    </LabelShortText>
  )
}

const LanguageSwitcherItemWrapper = styled.div`
  flex: 1;
  width: 100%;
`

const LabelShortText = styled.span`
  font-family: ${(props) => props.theme.font.monospace};
  text-transform: uppercase !important;
  color: unset !important;
`

const Dropdown = styled(DropdownBase)`
  display: block !important;
  margin: 0 !important;
`

const DropdownToggle = styled(DropdownToggleBase)`
  color: ${(props) => props.theme.bodyColor};
  padding: 0;
  margin: 0;
`

const DropdownMenu = styled(DropdownMenuInViewport)`
  background-color: ${(props) => props.theme.bodyBg};
  box-shadow: 1px 1px 20px 0 #0005;
  transition: opacity ease-out 0.25s;
  padding: 1rem;
  padding-right: 0;
  width: 320px;
`

const DropdownItem = styled(DropdownItemBase)`
  width: 100% !important;
  padding: 0 !important;
`

const SearchBoxWrapper = styled.div`
  margin-bottom: 0.5rem;
  margin-right: 1rem;
`

const DropdownMenuListWrapper = styled.div`
  height: calc(80vh - 150px);
  min-height: 100px;
  overflow-y: scroll;
`
